import { Events, Fields, Versions } from '@vimeo/bigpicture-client';
import getDeviceType from '@vimeo/ui/core/utils/getDeviceType';
import {
  buildActionBpContext,
  buildCollectionBpContext,
  buildProductAnalyticsBpContext,
  buildTeamBpContextFromTeamUser,
  buildThirdPartyIntegrationBpContext,
  buildVideoBpContext,
  buildVideoViewBpContext,
  buildViewBpContext,
  buildWebBpContext,
  CollectionBpContext,
  ProductAnalyticsBpContext,
  sendBpEventWithContexts,
  WebBpContext,
} from '@vimeo/use-tracking';
import { Viewer } from '@vimeo/viewer';

import { idFromUri } from './helper';
import { BpCollectionContextsType, BpCollectionPrivacy } from './types';

const buildDefaultContext = (viewer?: Viewer | null) => {
  const actionContext = defaultActionContext();
  const webContext = defaultWebContext();
  const teamContext = buildTeamBpContextFromTeamUser(viewer?.teamUser);

  return {
    ...actionContext,
    ...webContext,
    ...teamContext,
  };
};

const defaultWebContext = (webContextFields?: Partial<WebBpContext>) => {
  return buildWebBpContext({
    page_name: 'showcase_gallery',
    referrer_page_name: null,
    path: window.location.pathname,
    ...(!!document.referrer && { referrer: document.referrer }),
    ...webContextFields,
  });
};

const defaultActionContext = () => {
  return buildActionBpContext({
    action_type: 'click',
    feature: null,
  });
};

const defaultThirdPartyIntegrationContext = () => {
  return buildThirdPartyIntegrationBpContext({
    is_integration: false,
    integration_id: null,
    integration_name: null,
    managed_user_id: null,
    app_id: null,
    integration_type: null,
    partner_bucket: null,
    is_partner: null,
  });
};

const defaultVideoViewerContext = (sessionId: string) => {
  return buildVideoViewBpContext({
    viewer_home_session_id: sessionId,
  });
};

const defaultProductAnalyticsContext = (
  productAnalyticsContextFields?: Partial<ProductAnalyticsBpContext>,
) => {
  return buildProductAnalyticsBpContext({
    product: 'showcase',
    feature: 'watch',
    flow: 'view_showcase',
    location: 'not_applicable_pageview',
    device_type: getDeviceType(),
    ...productAnalyticsContextFields,
  });
};

export const bpShowcasePageLoad = (showcaseOwnerUri: string, viewer: Viewer) => {
  const showcaseOwnerId = idFromUri(showcaseOwnerUri).toString();
  const viewContext = buildViewBpContext({
    view_type: 'pageview',
    feature: null,
  });

  const webContext = defaultWebContext();
  const teamContext = buildTeamBpContextFromTeamUser(viewer?.teamUser);
  const productAnalyticsContext = defaultProductAnalyticsContext({
    element: 'screen',
  });

  const thirdPartyIntegrationContext = defaultThirdPartyIntegrationContext();

  const contexts = {
    ...viewContext,
    ...webContext,
    ...teamContext,
    ...productAnalyticsContext,
    ...thirdPartyIntegrationContext,
  };

  sendBpEventWithContexts(
    'vimeo.showcase_gallery_page_load',
    contexts,
    2 as Versions<Events>,
    {
      showcase_owner_id: showcaseOwnerId,
    } as Fields<Events, Versions<Events>>,
  );
};

export const bpShowcaseVideoClick = (
  showcaseOwnerUri: string,
  videoId: string,
  video_privacy: string,
  videoOwnerUri: string,
  viewer: Viewer,
) => {
  const showcase_owner_id = idFromUri(showcaseOwnerUri).toString();
  const video_owner_id = idFromUri(videoOwnerUri);
  const actionContext = defaultActionContext();
  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
    video_owner_id,
    video_privacy,
  });

  const webContext = defaultWebContext();
  const teamContext = buildTeamBpContextFromTeamUser(viewer?.teamUser);
  const productAnalyticsContext = defaultProductAnalyticsContext({
    element: 'thumbnail',
  });

  const thirdPartyIntegrationContext = defaultThirdPartyIntegrationContext();

  const contexts = {
    ...actionContext,
    ...videoContext,
    ...webContext,
    ...teamContext,
    ...productAnalyticsContext,
    ...thirdPartyIntegrationContext,
  };

  sendBpEventWithContexts(
    'vimeo.select_showcase_video',
    contexts,
    3 as Versions<Events>,
    {
      showcase_owner_id,
    } as Fields<Events, Versions<Events>>,
  );
};

export const bpShowcaseLoadVideo = (
  showcaseOwnerUri: string,
  videoId: string,
  video_privacy: string,
  videoOwnerUri: string,
  viewer: Viewer,
) => {
  const showcase_owner_id = idFromUri(showcaseOwnerUri).toString();
  const video_owner_id = idFromUri(videoOwnerUri);
  const actionContext = defaultActionContext();
  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
    video_owner_id,
    video_privacy,
  });

  const webContext = defaultWebContext({
    page_name: 'showcase_recipient',
  });

  const teamContext = buildTeamBpContextFromTeamUser(viewer?.teamUser);
  const productAnalyticsContext = defaultProductAnalyticsContext();
  const thirdPartyIntegrationContext = defaultThirdPartyIntegrationContext();

  const contexts = {
    ...actionContext,
    ...videoContext,
    ...webContext,
    ...teamContext,
    ...productAnalyticsContext,
    ...thirdPartyIntegrationContext,
  };

  sendBpEventWithContexts(
    'vimeo.page_load',
    contexts,
    2 as Versions<Events>,
    {
      showcase_owner_id,
    } as Fields<Events, Versions<Events>>,
  );
};

export const bpViewAutoplayedNextVideo = (
  videoId: string,
  viewer: Viewer | null,
  video_privacy: string,
  target_path: string,
) => {
  const teamContext = buildTeamBpContextFromTeamUser(viewer?.teamUser);

  const actionContext = defaultActionContext();
  const viewContext = buildViewBpContext({
    view_type: 'impression',
    feature: null,
  });
  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId) ?? '',
    video_privacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'showcase',
    feature: 'playlist',
    location: 'player',
    device_type: getDeviceType(),
  });

  const webContext = buildWebBpContext({
    page_name: 'showcase_recipient',
    path: window.location.pathname,
    target: null,
    target_path,
    ...(!!document.referrer && { referrer: document.referrer }),
  });

  const contexts = {
    ...teamContext,
    ...actionContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...webContext,
    ...viewContext,
  };

  sendBpEventWithContexts('vimeo.view_autoplayed_next_video', contexts, 2 as Versions<Events>);
};

export const bpSelectToggleAutoplay = (
  videoId: string,
  toggleValue: boolean,
  viewer: Viewer | null,
  video_privacy: string,
) => {
  const teamContext = buildTeamBpContextFromTeamUser(viewer?.teamUser);

  const actionContext = buildActionBpContext({
    action_type: 'click',
    feature: null,
  });

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId) ?? '',
    video_privacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'showcase',
    feature: 'playlist',
    location: 'bottom_panel',
    modal_name: 'playlist_modal',
    element: 'toggle',
    copy: 'autoplay',
    device_type: getDeviceType(),
    flow: null,
  });

  const webContext = buildWebBpContext({
    page_name: 'showcase_recipient',
    path: window.location.pathname,
    target: null,
    target_path: null,
    ...(!!document.referrer && { referrer: document.referrer }),
  });

  const contexts = {
    ...teamContext,
    ...actionContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...webContext,
  };

  if (toggleValue) {
    sendBpEventWithContexts(
      'vimeo.select_turn_on_autoplay',
      contexts,
      2 as Versions<Events>,
      {
        profile_element_type: '', // TODO: BP should not include this field. remove this once the event is updated
      } as Fields<Events, Versions<Events>>,
    );
  } else {
    sendBpEventWithContexts(
      'vimeo.select_turn_off_autoplay',
      contexts,
      2 as Versions<Events>,
      {
        profile_element_type: '', // TODO: BP should not include this field. remove this once the event is updated
      } as Fields<Events, Versions<Events>>,
    );
  }
};

export const bpShareButton = (
  videoId: string,
  sessionId: string,
  bpCollectionContexts: BpCollectionContextsType,
  viewer?: Viewer | null,
) => {
  const defaultContext = buildDefaultContext(viewer);
  const thirdPartyIntegrationContext = defaultThirdPartyIntegrationContext();

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
  });

  const collectionsContext = buildCollectionBpContext({
    entity_id: bpCollectionContexts?.entity_id ?? 0,
    collection_embed_privacy: null,
    number_of_videos: bpCollectionContexts?.number_of_videos ?? null,
    is_seo_on: bpCollectionContexts?.is_seo_on ?? null,
    collection_privacy: (bpCollectionContexts?.collection_privacy ?? null) as BpCollectionPrivacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'viewer_home',
    feature: 'share',
    location: 'bottom_panel',
    modal_name: null,
    flow: 'share_video_link',
    element: 'button',
    copy: 'share',
    entity_type: 'video',
    device_type: getDeviceType(),
  });

  const videoViewContext = defaultVideoViewerContext(sessionId);

  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...collectionsContext,
    ...thirdPartyIntegrationContext,
    ...videoViewContext,
  };

  const fields = {
    distribution_type: 'share',
    target_quality: null,
    target_resolution: null,
    target_file_details: null,
    number_of_items: null,
    embed_config: null,
    embed_custom_dimensions: null,
    is_internal: false,
    sharee_id: null,
    sharee_entity_permission: null,
    sharee_team_permission: null,
    is_send_email_notification: null,
    sharee_email: null,
    collection_type: null,
    target_transcript_language: null,
    embed_layout: null,
    embed_playback_toggles_on: null,
    embed_playback_toggles_off: null,
    lms_technical_standard: null,
    lms_scoring_method: null,
    lms_completion_threshold: null,
    distribution_tab: null,
    audio_language_selected_for_download: null,
    is_ai_translated: null,
    is_original_language_downloaded: null,
  };

  sendBpEventWithContexts(
    'vimeo.open_distribution_options',
    contexts,
    18 as Versions<Events>,
    fields as Fields<Events, Versions<Events>>,
  );
};

export const bpLikeButton = (
  videoId: string,
  sessionId: string,
  bpCollectionContexts: BpCollectionContextsType,
  viewer?: Viewer,
) => {
  const defaultContext = buildDefaultContext(viewer);

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
  });

  const collectionsContext = buildCollectionBpContext({
    entity_id: bpCollectionContexts?.entity_id ?? 0,
    collection_embed_privacy: null,
    number_of_videos: bpCollectionContexts?.number_of_videos ?? null,
    is_seo_on: bpCollectionContexts?.is_seo_on ?? null,
    collection_privacy: (bpCollectionContexts?.collection_privacy ?? null) as BpCollectionPrivacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'viewer_home',
    feature: 'like',
    location: 'bottom_panel',
    modal_name: null,
    flow: 'showcase',
    element: 'button',
    copy: 'like',
    entity_type: 'video',
    device_type: getDeviceType(),
  });

  const videoViewContext = defaultVideoViewerContext(sessionId);

  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...collectionsContext,
    ...videoViewContext,
  };

  sendBpEventWithContexts(
    'vimeo.select_like_button',
    contexts,
    4 as Versions<Events>,
    {
      is_timestamp_enabled: null,
      profile_element_type: null,
      default_transcript_language: null,
      widget_placement: null,
      widget_name: null,
    } as Fields<Events, Versions<Events>>,
  );
};

export const bpLayoutToggle = (
  videoId: string,
  sessionId: string,
  bpCollectionFields: CollectionBpContext,
  viewer: Viewer | null,
  isSideBySideActive: boolean,
  clipOwnerUri: string | undefined,
  clipPrivacy: string | undefined,
) => {
  const defaultContext = buildDefaultContext(viewer);

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    flow: 'showcase',
    entity_type: 'video',
    element: 'icon',
    location: 'comments_panel',
    feature: 'view',
    device_type: getDeviceType(),
    product: 'viewer_home',
    copy: null,
  });

  const collectionsContext = buildCollectionBpContext(bpCollectionFields);

  const video_owner_id = idFromUri(clipOwnerUri);
  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
    video_owner_id,
    video_privacy: clipPrivacy,
  });

  const videoViewContext = buildVideoViewBpContext({
    viewer_home_session_id: sessionId,
    viewer_home_view_mode: isSideBySideActive ? 'stack_view_mode' : 'side_by_side_mode',
  });

  const contexts = {
    ...defaultContext,
    ...productAnalyticsContext,
    ...collectionsContext,
    ...videoContext,
    ...videoViewContext,
  };

  const fields = {
    profile_element_type: null,
    is_timestamp_enabled: null,
    default_transcript_language: null,
    widget_placement: null,
    widget_name: null,
  };

  sendBpEventWithContexts(
    isSideBySideActive ? 'vimeo.select_stack_view_mode' : 'vimeo.select_side_by_side_mode',
    contexts,
    4 as Versions<Events>,
    fields as Fields<Events, Versions<Events>>,
  );
};
